<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { format, parseISO } from 'date-fns'
import { nl } from 'date-fns/locale'
import { useRoute } from 'vue-router'
import { useEstablishmentHttp } from '@/http/establishmentHttp'
import Icon from '@/components/partials/Icon.vue'
import type { Discount } from "@/types/http/Discount"

const props = defineProps<{ discounts: Discount[] }>()

const { t } = useI18n()
const route = useRoute()

const endDateText = (date: string) => t('discount.stops.at', {
  moment: format(parseISO(date), 'EEEE dd-MM', { locale: nl }),
})

const formatDays = (days: number[]) => {
  const dayKeys = {
    1: 'monday',
    2: 'tuesday',
    3: 'wednesday',
    4: 'thursday',
    5: 'friday',
    6: 'saturday',
    7: 'sunday',
  }

  const daysText = days.map((day: number) => t(`terms.${dayKeys[day]}`)).join(', ')

  return `${t('discounts.active_on')}: ${daysText}`
}

const establishmentHttp = useEstablishmentHttp()
</script>

<template>
  <div ref="container" class="discount-box" v-if="discounts.length > 0">
    <div ref="slider" class="discount-box__slider">
      <div v-for="discount in discounts" data-test-id="discount-item" class="discount-box__discount">
        <span class="discount-title"><Icon name="discount"/> {{ discount.name }}</span>
        <span class="discount-description">{{ discount.explanation }}</span>
        <span class="discount-enddate" v-if="discount.days.length">{{ formatDays(discount.days) }}</span>
        <span class="discount-enddate" v-if="!discount.days.length">{{ endDateText(discount.to) }}</span>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import "@/assets/css/mixins/breakpoints-up.scss";
@import "@/assets/css/mixins/styling.scss";

.discount-box {
  border-bottom: 1px dashed #e5e5e5;

  @include lg-up {
    border-bottom: 0;
    margin: 2.75rem 0 0;
  }

  &__discounts {
    display: flex;
    flex-direction: row;
    gap:     1.5rem;
  }

  &__discount {
    display:          flex;
    flex-direction:   column;
    justify-content:  center;
    align-items:      left;
    gap:              0;
    line-height:      1.125rem;
    background-color: #f8f8fa;
    border-radius:    0.5rem;
    padding:          0.5rem 1rem;
    font-size:        0.875rem;

    border-radius:    18px;

    @include lg-up {
      padding:     .8rem 1.2rem;
      line-height: 1.3rem;
    }

    &:deep(.b-icon-icon) {
      width:        0.9rem;
      vertical-align: middle;
      position:     relative;
      top:          -1px;
      margin-right: 2px;

      & path {
        fill: #ff2aa1;
      }
    }

    .discount-title {
      font-weight:          600;
      font-size:            0.875rem;
      text-transform:       uppercase;
      background:           linear-gradient(66deg, rgb(255, 42, 161) 0%, rgb(255, 157, 5) 100%);
      margin-bottom:        2px;

      -webkit-background-clip: text;
      -moz-background-clip: text;
      -webkit-text-fill-color: transparent;
      -moz-text-fill-color: transparent;
    }

    .discount-description {
      font-weight: 200;
      font-size: 0.8rem;
      white-space: nowrap;
    }

    .discount-enddate {
      font-weight: 200;
      font-size: 0.75rem;
      color:     #9a9797;
    }
  }

  &__slider {
    position:        relative;
    display:         flex;
    align-items:     center;
    gap:             0.5rem;
    user-select:     none;
    scrollbar-width: none; /* Firefox */
    scroll-snap-type: x;
    overflow-x:      scroll;
    margin:          auto;
    height:          90px;

    // no scrollbar
    &::-webkit-scrollbar {
      display: none;
    }

    .discount-box__discount:first-child {
      margin-left: 1rem;
    }

    @include sm-up {
      width: 34rem;
    }

    @include md-up {
      width:     46rem;
      font-size: 0.875rem;
      height:    88px;
    }

    @include lg-up {
      width:  58rem;
      height: auto;
      margin-bottom: 10px;

      .discount-box__discount:first-child {
        margin-left: 0;
      }
    }

    @include xl-up {
      width: 71rem;
    }

    @include xxl-up {
      width: 83.5rem;
    }

    @include xxxl-up {
      width: 90rem;
    }
  }
}
</style>
